import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  BarChart3,
  TrendingUp,
  PieChart as PieChartIcon,
  Download,
  Calendar,
  DollarSign,
  Users,
  Coins
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ["#0F2744", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4"];

export default function AnalyticsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("6m");

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [analyticsRes, performanceRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`),
        axios.get(`${API}/analytics/performance`)
      ]);
      setAnalytics(analyticsRes.data);
      setPerformance(performanceRes.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleExport = (format) => {
    // Simulated export
    console.log(`Exporting in ${format} format`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const industryPieData = analytics?.industry_breakdown?.map((item, idx) => ({
    name: item.industry,
    value: item.funds,
    color: CHART_COLORS[idx % CHART_COLORS.length]
  })) || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Analytics & Reports</h1>
          <p className="text-slate-500 mt-1">Platform performance metrics and insights</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32" data-testid="time-range-select">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1m">1 Month</SelectItem>
              <SelectItem value="3m">3 Months</SelectItem>
              <SelectItem value="6m">6 Months</SelectItem>
              <SelectItem value="1y">1 Year</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => handleExport("pdf")}
            data-testid="export-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport("excel")}
            data-testid="export-excel-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-slate-200" data-testid="metric-funds-raised">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Funds Raised</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {formatCurrency(analytics?.total_funds_raised || 0)}
                </p>
                <p className="text-sm text-emerald-600 flex items-center mt-2">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  +18.5% vs last period
                </p>
              </div>
              <div className="w-12 h-12 bg-[#E8F4FD] rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-[#3B82F6]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="metric-tokens-sold">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Tokens Sold</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {(analytics?.total_tokens_sold || 0).toLocaleString()}
                </p>
                <p className="text-sm text-emerald-600 flex items-center mt-2">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  +12.3% vs last period
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                <Coins className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="metric-investors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Investors</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {analytics?.total_investors || 0}
                </p>
                <p className="text-sm text-emerald-600 flex items-center mt-2">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  +8 new this week
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="metric-avg-roi">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Average ROI</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1">
                  {analytics?.average_roi?.toFixed(1)}%
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  Across all projects
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Investment Trends */}
        <Card className="border-slate-200" data-testid="investment-trends-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Investment Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics?.monthly_data || []}>
                  <defs>
                    <linearGradient id="colorInvestments" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0F2744" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0F2744" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorEscrow" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                  <YAxis
                    stroke="#94A3B8"
                    fontSize={12}
                    tickFormatter={(value) => `$${value / 1000}k`}
                  />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Area
                    type="monotone"
                    dataKey="investments"
                    stroke="#0F2744"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorInvestments)"
                    name="Investments"
                  />
                  <Area
                    type="monotone"
                    dataKey="escrow"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorEscrow)"
                    name="Escrow"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Industry Distribution */}
        <Card className="border-slate-200" data-testid="industry-distribution-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Funds by Industry</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex">
              <div className="h-72 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={industryPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {industryPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-48 flex flex-col justify-center space-y-2">
                {industryPieData.slice(0, 6).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-slate-600 truncate">{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Detailed Analytics */}
      <Tabs defaultValue="performance" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="performance" data-testid="tab-performance">Top Performers</TabsTrigger>
          <TabsTrigger value="distribution" data-testid="tab-distribution">Token Distribution</TabsTrigger>
          <TabsTrigger value="industry" data-testid="tab-industry-analysis">Industry Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="performance">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Top Performing Projects</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Rank</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Project</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Industry</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Funds Raised</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {performance?.top_performers?.map((project, idx) => (
                      <tr key={project.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`top-performer-${idx}`}>
                        <td className="py-4 px-4">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            idx === 0 ? "bg-amber-100 text-amber-700" :
                            idx === 1 ? "bg-slate-200 text-slate-700" :
                            idx === 2 ? "bg-orange-100 text-orange-700" :
                            "bg-slate-100 text-slate-500"
                          }`}>
                            {idx + 1}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={project.image_url}
                              alt={project.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                            <div>
                              <p className="font-medium text-slate-900">{project.name}</p>
                              <p className="text-xs text-slate-500 font-mono">{project.token_symbol}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="industry-tag">{project.industry}</span>
                        </td>
                        <td className="py-4 px-4 text-right font-medium text-slate-900">
                          {formatCurrency(project.funds_raised)}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="text-emerald-600 font-semibold flex items-center justify-end">
                            <TrendingUp className="w-4 h-4 mr-1" />
                            {project.roi_percentage}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="distribution">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Token Distribution Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={performance?.token_distribution || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="range" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#0F2744" radius={[4, 4, 0, 0]} name="Investors" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  Token distribution shows investor participation across different investment tiers. 
                  The majority of investors hold between 1,000-5,000 tokens.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="industry">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Industry Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics?.industry_breakdown || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis
                      type="number"
                      stroke="#94A3B8"
                      fontSize={12}
                      tickFormatter={(value) => `$${value / 1000}k`}
                    />
                    <YAxis
                      type="category"
                      dataKey="industry"
                      stroke="#94A3B8"
                      fontSize={11}
                      width={120}
                    />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="funds" fill="#3B82F6" radius={[0, 4, 4, 0]} name="Funds Raised" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
